import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import BlobField from '../components/BlobField';
import Toast from '../components/Toast';
import PerksShelf from '../components/PerksShelf';
import PerksSection from '../components/PerksSection';
import GrowthChip from '../components/GrowthChip';
import BountiesShelf from '../components/BountiesShelf';
import BountiesSection from '../components/BountiesSection';
import MembersStrip from '../components/MembersStrip';
import MembersDirectory from '../components/MembersDirectory';
import api, { formatApiErrorDetail, resolveAvatarUrl, uploadImage } from '../api';
import { useAuth } from '../context/AuthContext';
import { MicroBadges } from '../components/BadgeStrip';

/**
 * Phase 2 · Step 3 — Clan Feed / Live / Chat.
 * Reference: /app/reference/06-clan-feed.html
 *
 * Route: /clan/:handle/feed?tab=(feed|live|chat)
 * Membership gate: viewer must own bits_owned >= min_bits_to_join OR be the creator.
 * Non-members see the public paywall panel and a CTA to buy Bits (routes to /trade/:handle).
 */

const RELATIVE = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString();
};

const HHMM = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

function Avatar({ initials, gradient, size = 38, avatar }) {
  const grad = gradient && gradient.length >= 2
    ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
    : 'linear-gradient(135deg, #FF5722, #FFC107)';
  const src = avatar ? resolveAvatarUrl(avatar) : null;
  return (
    <div
      className="cf-avatar"
      style={{ width: size, height: size, background: grad, fontSize: size / 2.6 }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
      ) : (
        (initials || '??').slice(0, 2).toUpperCase()
      )}
    </div>
  );
}

export default function ClanFeed() {
  const { handle } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [access, setAccess] = useState(undefined); // undefined=loading, obj=result
  const [creator, setCreator] = useState(null);
  const [tab, setTab] = useState(sp.get('tab') || 'feed');
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [live, setLive] = useState({ is_live: false, session: null });
  const [composeOpen, setComposeOpen] = useState(sp.get('compose') === '1');
  const [liveComposeOpen, setLiveComposeOpen] = useState(sp.get('start') === '1');
  const [toast, setToast] = useState(null);
  // Badges cache — { handle: [badge_key, ...] }. Populated by a single bulk
  // fetch whenever the set of authors visible in posts/chat changes.
  const [badgesByHandle, setBadgesByHandle] = useState({});

  const chatScrollRef = useRef(null);

  const initialTab = sp.get('tab');
  useEffect(() => {
    if (initialTab && ['feed', 'live', 'chat', 'perks', 'bounties', 'members'].includes(initialTab)) setTab(initialTab);
  }, [initialTab]);

  // Sync tab to URL without adding history entries. IMPORTANT: preserve
  // `location.state` here — `setSearchParams` with `replace: true` drops
  // state by default, which would wipe the `{ from }` sentinel that our
  // `goBack()` relies on. Without this, Trade → Feed → Back would silently
  // fall through to the canonical fallback (/creator/{handle}) instead of
  // returning to the previous page.
  useEffect(() => {
    const next = new URLSearchParams(sp);
    next.set('tab', tab);
    setSp(next, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Bootstrap: access + creator profile
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/clans/${handle}/access`).catch((ex) => ({ err: ex })),
      api.get(`/creators/${handle}`).catch(() => null),
      api.get(`/clans/${handle}/live`).catch(() => ({ data: { is_live: false, session: null } })),
    ]).then(([acc, cr, l]) => {
      if (cancelled) return;
      if (acc.err) {
        setAccess({ error: formatApiErrorDetail(acc.err.response?.data?.detail) || 'Could not load Clan.' });
        return;
      }
      setAccess(acc.data);
      setCreator(cr?.data || null);
      setLive(l.data);
    });
    return () => { cancelled = true; };
  }, [handle]);

  // Fetch posts + chat when user has access
  const loadPosts = useCallback(async () => {
    try {
      const { data } = await api.get(`/clans/${handle}/posts`);
      setPosts(data.posts);
    } catch { /* ignore */ }
  }, [handle]);

  const loadChat = useCallback(async () => {
    try {
      const { data } = await api.get(`/clans/${handle}/chat`);
      setMessages(data.messages);
      // Scroll chat to bottom on refresh
      setTimeout(() => {
        if (chatScrollRef.current) {
          chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
      }, 40);
    } catch { /* ignore */ }
  }, [handle]);

  useEffect(() => {
    if (!access?.is_member) return;
    loadPosts();
    loadChat();
  }, [access?.is_member, loadPosts, loadChat]);

  // Bulk-fetch badges for every unique author across posts + chat messages.
  // Diffs the previous set so we only refetch when a new author appears.
  useEffect(() => {
    const handles = new Set();
    posts.forEach((p) => { if (p?.author?.handle) handles.add(p.author.handle); });
    messages.forEach((m) => { if (m?.user?.handle) handles.add(m.user.handle); });
    const missing = [...handles].filter((h) => !(h in badgesByHandle));
    if (missing.length === 0) return;
    api.post('/badges/bulk', { handles: missing })
      .then(({ data }) => {
        // Merge — keep already-fetched entries, add newly-resolved ones, and
        // record every requested handle (even with empty badges) so we don't refetch.
        setBadgesByHandle((prev) => {
          const next = { ...prev };
          missing.forEach((h) => { next[h] = data.by_handle?.[h] || []; });
          return next;
        });
      })
      .catch(() => {
        // On failure, still mark them as fetched (empty) so we don't spin
        setBadgesByHandle((prev) => {
          const next = { ...prev };
          missing.forEach((h) => { if (!(h in next)) next[h] = []; });
          return next;
        });
      });
  }, [posts, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll chat every 10s while user is on Chat tab
  useEffect(() => {
    if (!access?.is_member || tab !== 'chat') return;
    const t = setInterval(loadChat, 10000);
    return () => clearInterval(t);
  }, [access?.is_member, tab, loadChat]);

  // Poll live status every 20s while on Live tab
  useEffect(() => {
    if (tab !== 'live') return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/clans/${handle}/live`);
        setLive(data);
      } catch { /* ignore */ }
    }, 20000);
    return () => clearInterval(t);
  }, [tab, handle]);

  // ---- Loading / not-found guards ----

  if (access === undefined) {
    return (
      <>
        <BlobField />
        <div className="loading-shell" data-testid="cf-loading">loading…</div>
      </>
    );
  }

  if (access?.error) {
    return (
      <>
        <BlobField />
        <div className="cf-wrap"><div className="cf-panel-card" data-testid="cf-err">
          <h2 style={{ marginBottom: 8 }}>Can't load this Clan</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{access.error}</p>
          <button className="cf-primary-btn" onClick={() => navigate(-1)} style={{ marginTop: 14 }}>Back</button>
        </div></div>
      </>
    );
  }

  const clanName = creator?.display_name || handle;
  const clanCategories = creator?.categories || [];
  const gradient = creator?.gradient || ['#FF5722', '#FFC107'];
  const initials = creator?.initials || clanName.slice(0, 2).toUpperCase();
  const clanAvatar = creator?.avatar || null;
  const coverUrl = creator?.cover_url || null;
  // Banner uses uploaded cover image if creator set one, else auto-gradient.
  const bannerStyle = coverUrl
    ? { backgroundImage: `url(${coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: `linear-gradient(120deg, ${gradient[0]}, ${gradient[1]} 60%, #E040FB)` };
  const canonicalUrl = `/creator/${handle}`;
  // Back → wherever the user came from (deep-link from Trade, Home feed,
  // etc). We use `location.state?.from` as the reliable "came from
  // inside our SPA" signal — every in-app caller passes `state: { from }`
  // when navigating in. When absent (fresh tab / external referrer /
  // direct link), fall back to the Clan's canonical public page so we
  // never leak out of the SPA (window.history.length isn't reliable —
  // fresh tabs already have length >= 2 due to about:blank).
  const goBack = () => {
    if (location.state && location.state.from) navigate(-1);
    else navigate(canonicalUrl);
  };

  // ---- Non-member paywall ----
  if (!access.is_member) {
    return (
      <>
        <BlobField />
        <div className="cf-wrap" data-testid="cf-paywall">
          <div className="cf-topbar">
            <button className="cf-icon-btn" onClick={goBack} aria-label="Back" data-testid="cf-back-btn">
              <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div
            className="cf-banner"
            style={bannerStyle}
          >
            <div className="cf-banner-avatar" style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}>
              {clanAvatar ? (
                <img src={resolveAvatarUrl(clanAvatar)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
              ) : initials}
            </div>
          </div>
          <div className="cf-title-block">
            <div className="cf-title" data-testid="cf-clan-title">
              {clanName}'s Clan
              <span style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                <GrowthChip handle={handle} size="sm" />
              </span>
              <button
                type="button"
                className="cf-public-page-btn"
                onClick={() => navigate(`/creator/${handle}`)}
                title={`Open ${clanName}'s public profile`}
                data-testid="cf-public-page-btn-locked"
              >
                Public Page
              </button>
              <button
                type="button"
                className="cf-live-trading-btn"
                onClick={() => navigate(`/trade?clan=${handle}`)}
                title={`See ${clanName}'s live trading`}
                data-testid="cf-live-trading-btn-locked"
              >
                Live Trading
              </button>
            </div>
            <div className="cf-sub">{clanCategories[0] || 'Creator'}</div>
          </div>
          <div className="cf-locked-card" data-testid="cf-locked-card">
            <div className="cf-lock-icon">🔒</div>
            <div className="cf-lock-title">Members-only Clan</div>
            <p className="cf-lock-copy">
              Hold at least <b>{access.min_bits_to_join}</b> Bit{access.min_bits_to_join === 1 ? '' : 's'} to unlock {clanName}'s Feed, Live streams, and Chat. You currently own <b>{access.bits_owned || 0}</b>.
            </p>
            <button
              className="cf-primary-btn"
              onClick={() => navigate(`/trade/${handle}`)}
              data-testid="cf-buy-bits-btn"
            >
              Buy Bits to join
            </button>
          </div>

          {/* FOMO teaser — visible only when the creator is currently LIVE */}
          {live?.is_live && (
            <div className="cf-live-card cf-fomo-card" data-testid="cf-fomo-live-card">
              <div
                className="cf-live-video"
                style={live.session?.thumbnail_url ? {
                  backgroundImage: `url(${live.session.thumbnail_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                } : undefined}
                data-testid="cf-fomo-thumb"
              >
                <span className="cf-live-badge"><span className="dot" />LIVE NOW</span>
              </div>
              <div className="cf-live-info">
                <div className="cf-live-title-txt" data-testid="cf-fomo-title">
                  {live.session?.title || 'Live now'}
                </div>
                <div className="cf-live-meta">Started {RELATIVE(live.session?.started_at)}</div>
                {(live.session?.tags || []).length > 0 && (
                  <div className="cf-live-tags-display" data-testid="cf-fomo-tags">
                    {live.session.tags.map((t) => (
                      <span key={t} className="cf-live-tag-badge">#{t}</span>
                    ))}
                  </div>
                )}
                <button
                  className="cf-primary-btn"
                  style={{ width: '100%' }}
                  onClick={() => navigate(`/trade/${handle}`)}
                  data-testid="cf-fomo-join"
                >
                  Buy Bits to join live
                </button>
              </div>
            </div>
          )}

          {/* Perks preview — non-members see what's inside driving buy pressure.
              Every card will show a "Buy X more Bits to unlock" chip. */}
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, opacity: 0.85, textAlign: 'center' }}>
              ✨ What&apos;s inside {clanName}&apos;s Clan
            </div>
            <MembersStrip clanHandle={handle} />
            <PerksShelf clanHandle={handle} clanName={clanName} onToast={setToast} />
          </div>
        </div>
      </>
    );
  }

  // ---- Member/creator view ----
  const isCreator = access.is_creator;

  return (
    <>
      <BlobField />
      <div className="cf-wrap" data-testid="clan-feed-page">
        {/* Top bar */}
        <div className="cf-topbar">
          <button className="cf-icon-btn" onClick={goBack} aria-label="Back" data-testid="cf-back-btn">
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {isCreator && (
            <button
              className="cf-icon-btn"
              onClick={() => navigate('/manage-clan')}
              aria-label="Manage Clan"
              data-testid="cf-manage-btn"
              title="Manage Clan"
            >
              <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
                <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9v.1a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </button>
          )}
        </div>

        {/* Banner */}
        <div
          className="cf-banner"
          style={bannerStyle}
        >
          <div className="cf-banner-avatar" style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}>
            {clanAvatar ? (
              <img src={resolveAvatarUrl(clanAvatar)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
            ) : initials}
          </div>
        </div>
        <div className="cf-title-block">
          <div className="cf-title" data-testid="cf-clan-title">
            {clanName}'s Clan
            <span style={{ marginLeft: 8, verticalAlign: 'middle' }}>
              <GrowthChip handle={handle} size="sm" />
            </span>
            <button
              type="button"
              className="cf-public-page-btn"
              onClick={() => navigate(`/creator/${handle}`)}
              title={`Open ${clanName}'s public profile`}
              data-testid="cf-public-page-btn"
            >
              Public Page
            </button>
            <button
              type="button"
              className="cf-live-trading-btn"
              onClick={() => navigate(`/trade?clan=${handle}`)}
              title={`See ${clanName}'s live trading`}
              data-testid="cf-live-trading-btn"
            >
              Live Trading
            </button>
          </div>
          <div className="cf-sub">
            {access.bits_owned !== null && access.bits_owned !== undefined
              ? `Members-only · ${clanCategories[0] || 'Creator'}`
              : (clanCategories[0] || 'Creator')}
          </div>
        </div>

        {/* Membership chip */}
        <div className="cf-member-chip" data-testid="cf-member-chip">
          <svg viewBox="0 0 20 20" fill="none" width="15" height="15">
            <path d="M10 2l7 3v5c0 4.5-3 7.5-7 8-4-.5-7-3.5-7-8V5l7-3z" stroke="currentColor" strokeWidth="1.6" />
            <path d="M7 10l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {isCreator
            ? <span>You're the creator <span className="cf-chip-sub">· manage this Clan from settings</span></span>
            : <span>You're a member <span className="cf-chip-sub">· holding {access.bits_owned} Bit{access.bits_owned === 1 ? '' : 's'} (min. {access.min_bits_to_join} required)</span></span>
          }
        </div>

        {/* Tabs */}
        <div className="cf-tabs">
          <button
            className={`cf-tab${tab === 'feed' ? ' active' : ''}`}
            onClick={() => setTab('feed')}
            data-testid="cf-tab-feed"
          >Feed</button>
          <button
            className={`cf-tab${tab === 'live' ? ' active' : ''}`}
            onClick={() => setTab('live')}
            data-testid="cf-tab-live"
          >
            {live.is_live && <span className="cf-live-dot" />}Live <span className="cf-tab-soon" data-testid="cf-tab-live-soon">Coming soon</span>
          </button>
          <button
            className={`cf-tab${tab === 'chat' ? ' active' : ''}`}
            onClick={() => setTab('chat')}
            data-testid="cf-tab-chat"
          >Chat</button>
          <button
            className={`cf-tab${tab === 'perks' ? ' active' : ''}`}
            onClick={() => setTab('perks')}
            data-testid="cf-tab-perks"
          >Perks</button>
          <button
            className={`cf-tab${tab === 'bounties' ? ' active' : ''}`}
            onClick={() => setTab('bounties')}
            data-testid="cf-tab-bounties"
          >Bounties</button>
          <button
            className={`cf-tab${tab === 'members' ? ' active' : ''}`}
            onClick={() => setTab('members')}
            data-testid="cf-tab-members"
          >Members</button>
        </div>

        {/* FEED */}
        {tab === 'feed' && (
          <div data-testid="cf-panel-feed">
            {isCreator ? (
              <button
                className="cf-compose-cta"
                onClick={() => setComposeOpen(true)}
                data-testid="cf-open-compose"
              >
                <span className="cf-compose-plus">+</span> Write a new post…
              </button>
            ) : (
              <div className="cf-composer-note">
                <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
                  <path d="M10 2l7 3v5c0 4.5-3 7.5-7 8-4-.5-7-3.5-7-8V5l7-3z" stroke="currentColor" strokeWidth="1.6" />
                </svg>
                Only {clanName} can post here — members can like and comment.
              </div>
            )}

            {posts.length === 0 ? (
              <div className="cf-empty" data-testid="cf-feed-empty">
                <div className="cf-empty-emoji">📭</div>
                <div className="cf-empty-title">No posts yet</div>
                <p className="cf-empty-sub">
                  {isCreator ? 'Post your first update and welcome your Clan.' : `${clanName} hasn't posted anything yet — check back soon.`}
                </p>
              </div>
            ) : (
              posts.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  handle={handle}
                  isCreator={isCreator}
                  onChanged={loadPosts}
                  onToast={setToast}
                  badgeKeys={p?.author?.handle ? badgesByHandle[p.author.handle] : null}
                />
              ))
            )}
          </div>
        )}

        {/* LIVE */}
        {tab === 'live' && (
          <div data-testid="cf-panel-live">
            <div className="cf-live-soon-panel" data-testid="cf-live-soon-panel">
              <div className="cf-live-soon-emoji" aria-hidden="true">🎥</div>
              <div className="cf-live-soon-title">Live streaming is coming soon</div>
              <div className="cf-live-soon-sub">
                We're wiring up the video infrastructure so creators can go live directly to
                their Clan holders — chat overlay, tips, and everything in one room. Watch this space.
              </div>
            </div>
          </div>
        )}

        {/* CHAT */}
        {tab === 'chat' && (
          <div data-testid="cf-panel-chat">
            <ChatPanel
              messages={messages}
              handle={handle}
              me={user}
              scrollRef={chatScrollRef}
              onSent={loadChat}
              onToast={setToast}
              badgesByHandle={badgesByHandle}
            />
          </div>
        )}

        {tab === 'perks' && (
          isCreator
            ? <PerksSection clan={{ handle, min_bits_to_join: access.min_bits_to_join, multiplier: access.multiplier, bits_minted: access.bits_minted }} onToast={setToast} />
            : <PerksShelf clanHandle={handle} clanName={clanName} onToast={setToast} />
        )}

        {tab === 'bounties' && (
          isCreator
            ? <BountiesSection clanHandle={handle} onToast={setToast} />
            : <BountiesShelf clanHandle={handle} clanName={clanName} onToast={setToast} />
        )}

        {tab === 'members' && (
          <MembersDirectory clanHandle={handle} onToast={setToast} />
        )}
      </div>

      {composeOpen && (
        <ComposeSheet
          handle={handle}
          onClose={() => setComposeOpen(false)}
          onCreated={async () => {
            setComposeOpen(false);
            await loadPosts();
            setToast({ msg: '✓ Posted to your Clan' });
          }}
        />
      )}

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </>
  );
}

/* ================= Sub components ================= */

function PostCard({ post, handle, isCreator, onChanged, onToast, badgeKeys }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count);
  const confirmDelTimer = useRef(null);

  useEffect(() => () => { if (confirmDelTimer.current) clearTimeout(confirmDelTimer.current); }, []);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    // Optimistic
    setLiked((v) => !v);
    setLikes((v) => v + (liked ? -1 : 1));
    try {
      const { data } = await api.post(`/clans/${handle}/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(data.likes_count);
    } catch {
      // revert
      setLiked((v) => !v);
      setLikes((v) => v + (liked ? 1 : -1));
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async () => {
    try {
      await api.post(`/clans/${handle}/posts/${post.id}/pin`);
      onChanged();
    } catch (ex) {
      onToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not pin.', tone: 'error' });
    }
  };

  const del = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      if (confirmDelTimer.current) clearTimeout(confirmDelTimer.current);
      confirmDelTimer.current = setTimeout(() => setConfirmDelete(false), 3500);
      return;
    }
    if (confirmDelTimer.current) clearTimeout(confirmDelTimer.current);
    setConfirmDelete(false);
    try {
      await api.delete(`/clans/${handle}/posts/${post.id}`);
      onChanged();
      onToast({ msg: '🗑️ Post deleted' });
    } catch (ex) {
      onToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not delete.', tone: 'error' });
    }
  };

  return (
    <div className="cf-post-card" data-testid={`cf-post-${post.id}`}>
      <div className="cf-post-head">
        <Avatar initials={post.author.initials} gradient={post.author.gradient} avatar={post.author.avatar} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cf-post-name-row">
            <span className="cf-post-name">{post.author.display_name}</span>
            <MicroBadges badgeKeys={badgeKeys} />
            <span className="cf-post-handle">@{post.author.handle}</span>
          </div>
          <div className="cf-post-time">{RELATIVE(post.created_at)}</div>
        </div>
        {post.pinned && (
          <span className="cf-pin-badge" data-testid={`cf-pin-badge-${post.id}`}>
            <svg viewBox="0 0 20 20" fill="none" width="11" height="11">
              <path d="M10 2v6M6 10h8l-1 6h-6l-1-6z" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            Pinned
          </span>
        )}
        {post.exclusive && !post.pinned && (
          <span className="cf-exclusive-badge">🔒 Clan-only</span>
        )}
      </div>

      <p className="cf-post-body">{post.body}</p>

      {post.poll && <PollBlock post={post} handle={handle} onChanged={onChanged} />}

      {post.attachments && post.attachments.length > 0 && (
        <div className={`cf-attach-grid cf-attach-${Math.min(post.attachments.length, 4)}`} data-testid={`cf-attach-grid-${post.id}`}>
          {post.attachments.map((a, i) => (
            <a key={a.path} href={a.url} target="_blank" rel="noreferrer" className="cf-attach-tile" data-testid={`cf-attach-${post.id}-${i}`}>
              <img src={a.url} alt="attachment" loading="lazy" />
            </a>
          ))}
        </div>
      )}

      {post.media_url && (
        <div className="cf-post-media">
          {/\.(mp4|webm|mov)$/i.test(post.media_url) ? (
            <video src={post.media_url} controls style={{ width: '100%', borderRadius: 12 }} />
          ) : (
            <img src={post.media_url} alt="post media" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
        </div>
      )}

      <div className="cf-post-actions">
        <button
          className={`cf-pact cf-like${liked ? ' liked' : ''}`}
          onClick={toggleLike}
          disabled={busy}
          data-testid={`cf-like-${post.id}`}
        >
          <svg viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" width="17" height="17">
            <path d="M12 20s-7-4.4-9.5-9C1 7.8 2.8 4 6.5 4c2 0 3.5 1.3 4.5 3 1-1.7 2.5-3 4.5-3 3.7 0 5.5 3.8 4 7 -2.5 4.6-9.5 9-9.5 9z" />
          </svg>
          <span>{likes}</span>
        </button>
        <button className="cf-pact" data-testid={`cf-comment-${post.id}`} onClick={() => setCommentsOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="17" height="17">
            <path d="M4 5h16v11H8l-4 4V5z" strokeLinejoin="round" />
          </svg>
          <span>{commentsCount}</span>
        </button>
        {isCreator && (
          <div className="cf-post-creator-actions">
            <button className="cf-mini-btn" onClick={togglePin} data-testid={`cf-toggle-pin-${post.id}`}>
              {post.pinned ? 'Unpin' : 'Pin'}
            </button>
            <button className="cf-mini-btn cf-danger" onClick={del} data-testid={`cf-delete-${post.id}`}>
              {confirmDelete ? 'Tap again to confirm' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      {commentsOpen && (
        <CommentsDrawer
          handle={handle}
          postId={post.id}
          isCreator={isCreator}
          onClose={() => setCommentsOpen(false)}
          onCountChange={(delta) => setCommentsCount((c) => Math.max(0, c + delta))}
          onToast={onToast}
        />
      )}
    </div>
  );
}

function CommentsDrawer({ handle, postId, isCreator, onClose, onCountChange, onToast }) {
  const [comments, setComments] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/clans/${handle}/posts/${postId}/comments`)
      .then(({ data }) => setComments(data.comments || []))
      .catch(() => setComments([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/clans/${handle}/posts/${postId}/comments`, { body });
      setComments((prev) => [...(prev || []), data.comment]);
      setInput('');
      onCountChange(+1);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not comment.', tone: 'error' });
    } finally { setSending(false); }
  };

  const remove = async (commentId) => {
    try {
      await api.delete(`/clans/${handle}/posts/${postId}/comments/${commentId}`);
      setComments((prev) => (prev || []).filter((x) => x.id !== commentId));
      onCountChange(-1);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not delete.', tone: 'error' });
    }
  };

  return (
    <div className="cs-scrim" onClick={onClose}>
      <div className="cs-sheet" onClick={(e) => e.stopPropagation()} data-testid="comments-drawer">
        <div className="cs-head">
          <div className="cs-title">💬 Comments</div>
          <button className="cs-close" onClick={onClose} data-testid="comments-close">×</button>
        </div>
        <div className="comments-list" data-testid="comments-list">
          {comments === null && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-tertiary)' }}>loading…</div>}
          {comments && comments.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}>
              No comments yet. Be the first.
            </div>
          )}
          {comments && comments.map((c) => (
            <div className="comment-row" key={c.id} data-testid={`comment-${c.id}`}>
              <Avatar initials={c.author.initials} gradient={c.author.gradient} avatar={c.author.avatar} size={30} />
              <div className="comment-body">
                <div className="comment-head">
                  <b>{c.author.display_name}</b>
                  <span className="comment-handle">@{c.author.handle}</span>
                </div>
                <div className="comment-text">{c.body}</div>
              </div>
              {isCreator && (
                <button
                  className="cf-mini-btn cf-danger"
                  onClick={() => remove(c.id)}
                  data-testid={`comment-del-${c.id}`}
                  aria-label="Delete comment"
                >×</button>
              )}
            </div>
          ))}
        </div>
        <div className="cf-chat-input-row" style={{ marginTop: 12 }}>
          <input
            type="text"
            placeholder="Add a comment…"
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 800))}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            data-testid="comment-input"
          />
          <button
            className="cf-chat-send"
            onClick={send}
            disabled={!input.trim() || sending}
            data-testid="comment-send"
            aria-label="Send"
          >
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M3 11l18-8-8 18-2-8-8-2z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function PollBlock({ post, handle, onChanged }) {
  const [voting, setVoting] = useState(false);
  const [localPoll, setLocalPoll] = useState(post.poll);

  // Refresh when post prop changes (e.g. parent reload)
  useEffect(() => { setLocalPoll(post.poll); }, [post.poll]);

  const total = localPoll.total_votes || 0;
  const myVote = localPoll.my_vote;

  const vote = async (optionId) => {
    if (voting || optionId === myVote) return;
    setVoting(true);
    try {
      const { data } = await api.post(`/clans/${handle}/posts/${post.id}/poll/vote`, { option_id: optionId });
      setLocalPoll(data.post.poll);
      // Silent parent refresh in bg
      onChanged?.();
    } catch { /* ignore */ } finally { setVoting(false); }
  };

  return (
    <div className="cf-poll" data-testid={`cf-poll-${post.id}`}>
      <div className="cf-poll-q">{localPoll.question}</div>
      <div className="cf-poll-opts">
        {localPoll.options.map((opt) => {
          const pct = total > 0 ? Math.round((opt.votes_count / total) * 100) : 0;
          const isMine = myVote === opt.id;
          return (
            <button
              key={opt.id}
              className={`cf-poll-opt${isMine ? ' voted' : ''}${myVote ? ' has-voted' : ''}`}
              onClick={() => vote(opt.id)}
              disabled={voting}
              data-testid={`cf-poll-opt-${post.id}-${opt.id}`}
            >
              <span className="cf-poll-fill" style={{ width: `${pct}%` }} />
              <span className="cf-poll-txt">
                {isMine && <span className="cf-poll-check">✓</span>}
                {opt.text}
              </span>
              {myVote && <span className="cf-poll-pct">{pct}%</span>}
            </button>
          );
        })}
      </div>
      <div className="cf-poll-meta">
        {total} vote{total === 1 ? '' : 's'}{myVote ? ' · you voted' : ' · tap to vote'}
      </div>
    </div>
  );
}

function ComposeSheet({ handle, onClose, onCreated }) {
  const [mode, setMode] = useState('text'); // text | poll | attachments
  const [body, setBody] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [exclusive, setExclusive] = useState(false);
  // Poll state
  const [pollQ, setPollQ] = useState('');
  const [pollOpts, setPollOpts] = useState(['', '']);
  // Attachment state — { url: preview, path: '' or null while uploading, key: id }
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => () => {
    // revoke local preview blobs on unmount
    attachments.forEach((a) => a.blob && URL.revokeObjectURL(a.blob));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPollOpt = () => {
    if (pollOpts.length >= 4) return;
    setPollOpts((prev) => [...prev, '']);
  };
  const removePollOpt = (i) => {
    if (pollOpts.length <= 2) return;
    setPollOpts((prev) => prev.filter((_, idx) => idx !== i));
  };
  const setPollOpt = (i, v) => {
    setPollOpts((prev) => prev.map((x, idx) => (idx === i ? v.slice(0, 80) : x)));
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    const room = 4 - attachments.length;
    const chosen = files.slice(0, room);
    for (const f of chosen) {
      if (!/^image\//.test(f.type)) {
        setErr('Only image files are allowed.');
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        setErr(`"${f.name}" is larger than 5 MB.`);
        continue;
      }
      const key = Math.random().toString(36).slice(2);
      const previewUrl = URL.createObjectURL(f);
      setAttachments((prev) => [...prev, { key, blob: previewUrl, path: null, uploading: true }]);
      try {
        const { path } = await uploadImage(f);
        setAttachments((prev) => prev.map((a) => a.key === key ? { ...a, path, uploading: false } : a));
      } catch (ex) {
        setAttachments((prev) => prev.filter((a) => a.key !== key));
        URL.revokeObjectURL(previewUrl);
        setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Upload failed.');
      }
    }
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => {
      const dropped = prev.find((a) => a.key === key);
      if (dropped?.blob) URL.revokeObjectURL(dropped.blob);
      return prev.filter((a) => a.key !== key);
    });
  };

  const uploading = attachments.some((a) => a.uploading);

  const canSubmit = () => {
    if (!body.trim() || submitting || uploading) return false;
    if (mode === 'poll') {
      const cleaned = pollOpts.map((x) => x.trim()).filter(Boolean);
      return pollQ.trim().length > 0 && cleaned.length >= 2;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    setErr('');
    const payload = {
      body: body.trim(),
      exclusive,
    };
    if (mode === 'text') {
      payload.media_url = mediaUrl.trim() || null;
    } else if (mode === 'poll') {
      payload.poll_question = pollQ.trim();
      payload.poll_options = pollOpts.map((x) => x.trim()).filter(Boolean);
    } else if (mode === 'attachments') {
      payload.attachments = attachments.map((a) => a.path).filter(Boolean);
      if (payload.attachments.length === 0) {
        setErr('Add at least one image or switch to Text.');
        setSubmitting(false);
        return;
      }
    }
    try {
      await api.post(`/clans/${handle}/posts`, payload);
      onCreated();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not post.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cf-sheet-backdrop" onClick={onClose}>
      <div className="cf-sheet" onClick={(e) => e.stopPropagation()} data-testid="cf-compose-sheet">
        <div className="cf-sheet-head">
          <div className="cf-sheet-title">New post</div>
          <button className="cf-sheet-close" onClick={onClose} aria-label="Close" data-testid="cf-compose-close">×</button>
        </div>

        {/* Mode picker */}
        <div className="cf-mode-picker" role="tablist">
          {[
            { k: 'text', label: 'Text', icon: '✍️' },
            { k: 'poll', label: 'Poll', icon: '📊' },
            { k: 'attachments', label: 'Images', icon: '🖼️' },
          ].map((m) => (
            <button
              key={m.k}
              className={`cf-mode-chip${mode === m.k ? ' active' : ''}`}
              onClick={() => { setMode(m.k); setErr(''); }}
              data-testid={`cf-mode-${m.k}`}
              type="button"
            >
              <span aria-hidden>{m.icon}</span> {m.label}
            </button>
          ))}
        </div>

        <textarea
          className="cf-compose-body"
          placeholder={mode === 'poll' ? 'What are we deciding on?' : 'Share something with your Clan…'}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 2000))}
          rows={5}
          data-testid="cf-compose-body"
        />
        <div className="cf-compose-count">{body.length}/2000</div>

        {mode === 'text' && (
          <input
            type="url"
            className="cf-compose-input"
            placeholder="Image or video URL (optional)"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value.slice(0, 500))}
            data-testid="cf-compose-media"
          />
        )}

        {mode === 'poll' && (
          <div className="cf-poll-editor" data-testid="cf-poll-editor">
            <input
              type="text"
              className="cf-compose-input"
              placeholder="Poll question"
              value={pollQ}
              onChange={(e) => setPollQ(e.target.value.slice(0, 200))}
              data-testid="cf-poll-question"
            />
            {pollOpts.map((val, i) => (
              <div className="cf-poll-opt-row" key={i}>
                <input
                  type="text"
                  className="cf-compose-input"
                  placeholder={`Option ${i + 1}`}
                  value={val}
                  onChange={(e) => setPollOpt(i, e.target.value)}
                  data-testid={`cf-poll-opt-input-${i}`}
                  style={{ flex: 1 }}
                />
                {pollOpts.length > 2 && (
                  <button
                    type="button"
                    className="cf-mini-btn cf-danger"
                    onClick={() => removePollOpt(i)}
                    data-testid={`cf-poll-opt-remove-${i}`}
                    aria-label="Remove option"
                  >−</button>
                )}
              </div>
            ))}
            {pollOpts.length < 4 && (
              <button
                type="button"
                className="cf-secondary-btn"
                onClick={addPollOpt}
                data-testid="cf-poll-add-opt"
                style={{ width: '100%', marginTop: 4 }}
              >
                + Add option
              </button>
            )}
            <div className="cf-compose-hint">2–4 options · single choice · always open</div>
          </div>
        )}

        {mode === 'attachments' && (
          <div className="cf-attach-editor" data-testid="cf-attach-editor">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onPickFiles}
              style={{ display: 'none' }}
              data-testid="cf-attach-file-input"
            />
            <div className={`cf-attach-preview-grid cf-attach-${Math.max(1, attachments.length)}`}>
              {attachments.map((a) => (
                <div className="cf-attach-preview" key={a.key} data-testid={`cf-attach-preview-${a.key}`}>
                  <img src={a.blob} alt="preview" />
                  {a.uploading && <div className="cf-attach-uploading">uploading…</div>}
                  <button
                    type="button"
                    className="cf-attach-remove"
                    onClick={() => removeAttachment(a.key)}
                    aria-label="Remove attachment"
                    data-testid={`cf-attach-remove-${a.key}`}
                  >×</button>
                </div>
              ))}
              {attachments.length < 4 && (
                <button
                  type="button"
                  className="cf-attach-add"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="cf-attach-add"
                >
                  <span className="cf-attach-add-plus">+</span>
                  <span className="cf-attach-add-lbl">Add image</span>
                </button>
              )}
            </div>
            <div className="cf-compose-hint">Up to 4 images · JPG / PNG / WEBP · 5 MB each</div>
          </div>
        )}

        <label className="cf-compose-toggle">
          <input
            type="checkbox"
            checked={exclusive}
            onChange={(e) => setExclusive(e.target.checked)}
            data-testid="cf-compose-exclusive"
          />
          <span>🔒 Mark as Clan-only exclusive</span>
        </label>

        {err && <div className="cf-compose-err" data-testid="cf-compose-err">{err}</div>}
        <button
          className="cf-primary-btn"
          onClick={submit}
          disabled={!canSubmit()}
          data-testid="cf-compose-submit"
          style={{ width: '100%', marginTop: 8 }}
        >
          {submitting ? 'Posting…' : uploading ? 'Uploading images…' : 'Post to Clan'}
        </button>
      </div>
    </div>
  );
}

function LivePanel({ live, handle, isCreator, onChanged, openCompose, onCloseCompose, onToast }) {
  const [title, setTitle] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [thumb, setThumb] = useState(null); // { blob, path, uploading }
  const thumbInputRef = useRef(null);
  const [showTitle, setShowTitle] = useState(openCompose);
  const [submitting, setSubmitting] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const confirmEndTimer = useRef(null);

  useEffect(() => { if (openCompose) setShowTitle(true); }, [openCompose]);
  useEffect(() => () => {
    if (confirmEndTimer.current) clearTimeout(confirmEndTimer.current);
    if (thumb?.blob) URL.revokeObjectURL(thumb.blob);
  }, [thumb]);

  const addTag = () => {
    const t = tagInput.trim().slice(0, 24);
    if (!t) return;
    if (tags.length >= 5) return;
    if (tags.includes(t)) { setTagInput(''); return; }
    setTags((prev) => [...prev, t]);
    setTagInput('');
  };
  const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

  const pickThumb = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      onToast?.({ msg: 'Only image files allowed for the thumbnail.', tone: 'error' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onToast?.({ msg: 'Thumbnail must be ≤ 5 MB.', tone: 'error' });
      return;
    }
    // revoke prior preview
    if (thumb?.blob) URL.revokeObjectURL(thumb.blob);
    const previewUrl = URL.createObjectURL(file);
    setThumb({ blob: previewUrl, path: null, uploading: true });
    try {
      const { path } = await uploadImage(file);
      setThumb({ blob: previewUrl, path, uploading: false });
    } catch (ex) {
      URL.revokeObjectURL(previewUrl);
      setThumb(null);
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Upload failed.', tone: 'error' });
    }
  };

  const clearThumb = () => {
    if (thumb?.blob) URL.revokeObjectURL(thumb.blob);
    setThumb(null);
  };

  const start = async () => {
    if (!title.trim() || submitting || thumb?.uploading) return;
    setSubmitting(true);
    try {
      await api.post(`/clans/${handle}/live`, {
        action: 'start',
        title: title.trim(),
        thumbnail_path: thumb?.path || null,
        tags,
      });
      await onChanged();
      setTitle('');
      setTags([]);
      clearThumb();
      setTagInput('');
      setShowTitle(false);
      onCloseCompose?.();
      onToast?.({ msg: '🔴 You are LIVE' });
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not go live.', tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const end = async () => {
    // Two-tap in-app confirm (browser confirm() is unreliable in embedded contexts)
    if (!confirmEnd) {
      setConfirmEnd(true);
      if (confirmEndTimer.current) clearTimeout(confirmEndTimer.current);
      confirmEndTimer.current = setTimeout(() => setConfirmEnd(false), 3500);
      return;
    }
    if (confirmEndTimer.current) clearTimeout(confirmEndTimer.current);
    setConfirmEnd(false);
    try {
      await api.post(`/clans/${handle}/live`, { action: 'end' });
      await onChanged();
      onToast?.({ msg: '📴 Live ended' });
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not end live.', tone: 'error' });
    }
  };

  if (!live.is_live) {
    return (
      <div>
        <div className="cf-empty" data-testid="cf-live-empty">
          <div className="cf-empty-emoji">📴</div>
          <div className="cf-empty-title">No live stream right now</div>
          <p className="cf-empty-sub">
            {isCreator
              ? 'Start a live session — your Clan gets notified instantly.'
              : 'Check back when the creator goes live.'}
          </p>
        </div>
        {isCreator && (
          <div className="cf-live-composer" data-testid="cf-live-composer">
            {!showTitle ? (
              <button
                className="cf-live-cta"
                onClick={() => setShowTitle(true)}
                data-testid="cf-live-start-cta"
              >
                <span className="cf-live-dot" />
                Go Live
              </button>
            ) : (
              <div className="cf-live-editor" data-testid="cf-live-editor">
                <input
                  type="text"
                  className="cf-compose-input"
                  placeholder="What's this stream about?"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 120))}
                  data-testid="cf-live-title-input"
                  autoFocus
                />

                {/* Thumbnail picker */}
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  onChange={pickThumb}
                  style={{ display: 'none' }}
                  data-testid="cf-live-thumb-input"
                />
                {thumb ? (
                  <div className="cf-live-thumb-preview" data-testid="cf-live-thumb-preview">
                    <img src={thumb.blob} alt="Thumbnail preview" />
                    {thumb.uploading && <div className="cf-attach-uploading">uploading…</div>}
                    <button
                      type="button"
                      className="cf-attach-remove"
                      onClick={clearThumb}
                      data-testid="cf-live-thumb-remove"
                      aria-label="Remove thumbnail"
                    >×</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="cf-live-thumb-add"
                    onClick={() => thumbInputRef.current?.click()}
                    data-testid="cf-live-thumb-add"
                  >
                    <span className="cf-attach-add-plus">+</span>
                    <span>Add cover thumbnail (optional)</span>
                  </button>
                )}

                {/* Tags editor */}
                <div className="cf-live-tag-editor" data-testid="cf-live-tag-editor">
                  <div className="cf-live-tag-row">
                    <input
                      type="text"
                      className="cf-compose-input"
                      placeholder="Add a tag (e.g. Q&A)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value.slice(0, 24))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
                      }}
                      disabled={tags.length >= 5}
                      data-testid="cf-live-tag-input"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="cf-mini-btn"
                      onClick={addTag}
                      disabled={!tagInput.trim() || tags.length >= 5}
                      data-testid="cf-live-tag-add"
                    >+ Add</button>
                  </div>
                  {tags.length > 0 && (
                    <div className="cf-live-tag-chips" data-testid="cf-live-tag-chips">
                      {tags.map((t) => (
                        <span key={t} className="cf-live-tag-chip" data-testid={`cf-live-tag-chip-${t}`}>
                          #{t}
                          <button
                            type="button"
                            onClick={() => removeTag(t)}
                            aria-label={`Remove tag ${t}`}
                            data-testid={`cf-live-tag-remove-${t}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="cf-compose-hint">Up to 5 tags · 24 chars each</div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="cf-secondary-btn"
                    onClick={() => {
                      setShowTitle(false); setTitle(''); setTags([]); setTagInput(''); clearThumb();
                      onCloseCompose?.();
                    }}
                    data-testid="cf-live-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    className="cf-primary-btn"
                    style={{ flex: 1 }}
                    disabled={!title.trim() || submitting || thumb?.uploading}
                    onClick={start}
                    data-testid="cf-live-start"
                  >
                    {submitting ? 'Going live…' : thumb?.uploading ? 'Uploading thumbnail…' : 'Start now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const thumbnailUrl = live.session?.thumbnail_url || null;
  const liveTags = live.session?.tags || [];

  return (
    <div>
      <div className="cf-live-card" data-testid="cf-live-active">
        <div
          className="cf-live-video"
          style={thumbnailUrl ? {
            backgroundImage: `url(${thumbnailUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : undefined}
          data-testid="cf-live-thumb-display"
        >
          <span className="cf-live-badge"><span className="dot" />LIVE</span>
          {!thumbnailUrl && (
            <svg viewBox="0 0 24 24" fill="none" width="44" height="44">
              <path d="M8 5v14l11-7z" fill="rgba(255,255,255,0.55)" />
            </svg>
          )}
        </div>
        <div className="cf-live-info">
          <div className="cf-live-title-txt" data-testid="cf-live-title-display">
            {live.session?.title || 'Live now'}
          </div>
          <div className="cf-live-meta">Started {RELATIVE(live.session?.started_at)}</div>
          {liveTags.length > 0 && (
            <div className="cf-live-tags-display" data-testid="cf-live-tags-display">
              {liveTags.map((t) => (
                <span key={t} className="cf-live-tag-badge">#{t}</span>
              ))}
            </div>
          )}
          {isCreator ? (
            <button
              className="cf-primary-btn cf-danger-btn"
              style={{ width: '100%' }}
              onClick={end}
              data-testid="cf-live-end"
            >
              {confirmEnd ? 'Tap again to confirm — End live' : 'End live session'}
            </button>
          ) : (
            <button
              className="cf-primary-btn"
              style={{ width: '100%' }}
              onClick={() => onToast?.({ msg: '🎥 Live video streaming — coming soon. Say hi in Chat!' })}
              data-testid="cf-live-join"
            >
              Join Live Stream
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatPanel({ messages, handle, me, scrollRef, onSent, onToast, badgesByHandle }) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post(`/clans/${handle}/chat`, { body });
      setInput('');
      onSent();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not send.', tone: 'error' });
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const myId = me?.id;

  return (
    <div className="cf-chat-card">
      <div className="cf-chat-system">🎉 Be kind. No spam. Keep it Clan-friendly.</div>
      <div className="cf-chat-scroll" ref={scrollRef} data-testid="cf-chat-scroll">
        {messages.length === 0 ? (
          <div className="cf-empty" style={{ padding: '10px 4px 20px' }}>
            <div className="cf-empty-title" style={{ fontSize: 14 }}>No messages yet</div>
            <p className="cf-empty-sub">Break the ice — say hi to the Clan.</p>
          </div>
        ) : (
          messages.map((m) => {
            const isMe = m.user.id === myId;
            return (
              <div className={`cf-chat-msg${isMe ? ' me' : ''}`} key={m.id} data-testid={`cf-msg-${m.id}`}>
                <Avatar initials={m.user.initials} gradient={m.user.gradient} avatar={m.user.avatar} size={28} />
                <div className="cf-chat-bubble">
                  <b>
                    {isMe ? 'You' : m.user.display_name}
                    {!isMe && <MicroBadges badgeKeys={badgesByHandle?.[m.user.handle]} />}
                  </b>
                  {m.body}
                  <span className="cf-chat-time">{HHMM(m.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="cf-chat-input-row">
        <input
          type="text"
          placeholder="Message the Clan…"
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 500))}
          onKeyDown={onKey}
          data-testid="cf-chat-input"
        />
        <button
          className="cf-chat-send"
          onClick={send}
          disabled={!input.trim() || sending}
          data-testid="cf-chat-send"
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
            <path d="M3 11l18-8-8 18-2-8-8-2z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
